import React, { Suspense } from 'react';
import { BrowserRouter, Route, Routes } from 'react-router-dom';
import AppLayout from '../screens/layout/AppLayout';
import PublicRoute from './PublicRoute';
import PrivateRoute from './PrivateRoute';
import { Spin } from 'antd';
import Logout from '../screens/auth/Logout';
import UserCreate from '../screens/user/Create';
import Group from '../screens/group';
import GroupCreate from '../screens/group/Create';
import GroupEdit from '../screens/group/Edit';
import GroupDetail from '../screens/group/Detail';
import Company from '../screens/company';
import CompanyCreate from '../screens/company/Create';
import CompanyEdit from '../screens/company/Edit';
import CompanyDetail from '../screens/company/Detail';
import Asset from '../screens/asset';
import AssetCreate from '../screens/asset/Create';
import AssetDetail from '../screens/asset/Detail';
import AssetEdit from '../screens/asset/Edit';
import Device from '../screens/device';
import DeviceCreate from '../screens/device/Create';
import DeviceDetail from '../screens/device/Detail';
import DeviceEdit from '../screens/device/Edit';
import DeviceType from '../screens/deviceType';
import DeviceTypeCreate from '../screens/deviceType/Create';
import DeviceTypeDetail from '../screens/deviceType/Detail';
import DeviceTypeEdit from '../screens/deviceType/Edit';
import OperationMode from '../screens/operationMode';
import OperationModeCreate from '../screens/operationMode/Create';
import OperationModeEdit from '../screens/operationMode/Edit';
import MasterReport from '../screens/masterReport';
import MasterReportEmailReceiverEdit from '../screens/masterReport/emailReceiver/Edit';
import MasterReportEmailTemplateEdit from '../screens/masterReport/emailTemplate/Edit';
import MasterReportEmailTargetAssetEdit from '../screens/masterReport/emailTargetAsset/Edit';
import ReportLog from '../screens/report/log';
import DailyReport from '../screens/report/dailyReport';
import HomeCustom from '../screens/home/HomeCustom';
import PrivacyPolicy from '../screens/appConfig/PrivacyPolicy';
import TermsAndConditions from '../screens/appConfig/TermsAndCondition';
import TermsAndConditionsPublic from '../screens/appConfig/public/TermsAndConditionsPublic';
import PrivacyPolicyPublic from '../screens/appConfig/public/PrivacyPolicyPublic';

const NotFound = React.lazy(() => import('../NotFound'));

const Login = React.lazy(() => import('../screens/auth/NewLogin'));
const ForgotPassword = React.lazy(
  () => import('../screens/auth/ForgotPassword')
);
const ResetPassword = React.lazy(() => import('../screens/auth/ResetPassword'));
const Home = React.lazy(() => import('../screens/home'));
const Dashboard = React.lazy(() => import('../screens/dashboard'));
const Profile = React.lazy(() => import('../screens/profile'));
const ChangeMyPassword = React.lazy(
  () => import('../screens/profile/ChangeMyPassword')
);
const ChangePasswordUser = React.lazy(
  () => import('../screens/user/ChangePassword')
);
const User = React.lazy(() => import('../screens/user'));
const UserEdit = React.lazy(() => import('../screens/user/Edit'));
const UserDetail = React.lazy(() => import('../screens/user/Detail'));

const Role = React.lazy(() => import('../screens/role'));
const RoleEdit = React.lazy(() => import('../screens/role/Edit'));
const RoleDetail = React.lazy(() => import('../screens/role/Detail'));
const Client = React.lazy(() => import('../screens/client'));

const UserAddressComponent = React.lazy(
  () => import('../screens/user/userAddress')
);
const UserAddressDetail = React.lazy(
  () => import('../screens/user/userAddress/Detail')
);
const UserAddressEdit = React.lazy(
  () => import('../screens/user/userAddress/Edit')
);

const RootNavigator: React.FC = () => {
  const _renderLoading = () => {
    return (
      <div className='flex items-center justify-center w-screen h-screen'>
        <Spin spinning={true} />
      </div>
    );
  };

  const basename = process.env.REACT_APP_CUSTOM_BASENAME || '/';

  return (
    <BrowserRouter basename={basename}>
      <Routes>
        <Route
          path='/'
          element={
            <PublicRoute>
              <Suspense
                fallback={<React.Fragment>{_renderLoading()}</React.Fragment>}
              >
                <Login />
              </Suspense>
            </PublicRoute>
          }
        />
        <Route
          path='/login'
          element={
            <PublicRoute>
              <Suspense
                fallback={<React.Fragment>{_renderLoading()}</React.Fragment>}
              >
                <Login />
              </Suspense>
            </PublicRoute>
          }
        />
        <Route
          path='/forgot-password'
          element={
            <PublicRoute>
              <Suspense
                fallback={<React.Fragment>{_renderLoading()}</React.Fragment>}
              >
                <ForgotPassword />
              </Suspense>
            </PublicRoute>
          }
        />
        <Route
          path='/reset-password/:resetType/:sessionId'
          element={
            <PublicRoute>
              <Suspense
                fallback={<React.Fragment>{_renderLoading()}</React.Fragment>}
              >
                <ResetPassword />
              </Suspense>
            </PublicRoute>
          }
        />
        <Route path='/report-pdf' element={<DailyReport />} />

        <Route
          path='/public/privacy-policy'
          element={<PrivacyPolicyPublic />}
        />
        <Route
          path='/public/terms-and-conditions'
          element={<TermsAndConditionsPublic />}
        />

        <Route path='/custom-home' element={<HomeCustom />} />

        <Route path='*' element={<NotFound />} />

        <Route element={<AppLayout />}>
          <Route
            path='/home'
            element={
              <PrivateRoute>
                <Home />
              </PrivateRoute>
            }
          />
          <Route
            path='/dashboard'
            element={
              <PrivateRoute>
                <Dashboard />
              </PrivateRoute>
            }
          />
          <Route
            path='/profile'
            element={
              <PrivateRoute>
                <Profile />
              </PrivateRoute>
            }
          />
          <Route
            path='/change-password'
            element={
              <PrivateRoute>
                <ChangeMyPassword />
              </PrivateRoute>
            }
          />

          {/* Report */}
          <Route
            path='/report-log'
            element={
              <PrivateRoute>
                <ReportLog />
              </PrivateRoute>
            }
          />

          {/* Device Route */}
          <Route
            path='/device'
            element={
              <PrivateRoute>
                <Device />
              </PrivateRoute>
            }
          />
          <Route
            path='/device/add'
            element={
              <PrivateRoute>
                <DeviceCreate />
              </PrivateRoute>
            }
          />
          <Route
            path='/device/:deviceId'
            element={
              <PrivateRoute>
                <DeviceDetail />
              </PrivateRoute>
            }
          />
          <Route
            path='/device/:deviceId/edit'
            element={
              <PrivateRoute>
                <DeviceEdit />
              </PrivateRoute>
            }
          />

          {/* Device Type Route */}
          <Route
            path='/device-type'
            element={
              <PrivateRoute>
                <DeviceType />
              </PrivateRoute>
            }
          />
          <Route
            path='/device-type/add'
            element={
              <PrivateRoute>
                <DeviceTypeCreate />
              </PrivateRoute>
            }
          />
          <Route
            path='/device-type/:deviceTypeId'
            element={
              <PrivateRoute>
                <DeviceTypeDetail />
              </PrivateRoute>
            }
          />
          <Route
            path='/device-type/:deviceTypeId/edit'
            element={
              <PrivateRoute>
                <DeviceTypeEdit />
              </PrivateRoute>
            }
          />

          {/* Asset Route */}
          <Route
            path='/asset'
            element={
              <PrivateRoute>
                <Asset />
              </PrivateRoute>
            }
          />
          <Route
            path='/asset/add'
            element={
              <PrivateRoute>
                <AssetCreate />
              </PrivateRoute>
            }
          />
          <Route
            path='/asset/:assetId'
            element={
              <PrivateRoute>
                <AssetDetail />
              </PrivateRoute>
            }
          />
          <Route
            path='/asset/:assetId/edit'
            element={
              <PrivateRoute>
                <AssetEdit />
              </PrivateRoute>
            }
          />

          {/* Company Route */}
          <Route
            path='/company'
            element={
              <PrivateRoute>
                <Company />
              </PrivateRoute>
            }
          />
          <Route
            path='/company/add'
            element={
              <PrivateRoute>
                <CompanyCreate />
              </PrivateRoute>
            }
          />
          <Route
            path='/company/:companyId'
            element={
              <PrivateRoute>
                <CompanyDetail />
              </PrivateRoute>
            }
          />
          <Route
            path='/company/:companyId/edit'
            element={
              <PrivateRoute>
                <CompanyEdit />
              </PrivateRoute>
            }
          />

          {/* Group Route */}
          <Route
            path='/group'
            element={
              <PrivateRoute>
                <Group />
              </PrivateRoute>
            }
          />
          <Route
            path='/group/add'
            element={
              <PrivateRoute>
                <GroupCreate />
              </PrivateRoute>
            }
          />
          <Route
            path='/group/:groupId'
            element={
              <PrivateRoute>
                <GroupDetail />
              </PrivateRoute>
            }
          />
          <Route
            path='/group/:groupId/edit'
            element={
              <PrivateRoute>
                <GroupEdit />
              </PrivateRoute>
            }
          />

          {/* Master Report Route */}
          <Route
            path='/master-report'
            element={
              <PrivateRoute>
                <MasterReport />
              </PrivateRoute>
            }
          />
          <Route
            path='/master-report/email-receiver/add'
            element={
              <PrivateRoute>
                <MasterReportEmailReceiverEdit />
              </PrivateRoute>
            }
          />
          <Route
            path='/master-report/email-receiver/:reportEmailReceiverId/edit'
            element={
              <PrivateRoute>
                <MasterReportEmailReceiverEdit />
              </PrivateRoute>
            }
          />
          <Route
            path='/master-report/email-template/add'
            element={
              <PrivateRoute>
                <MasterReportEmailTemplateEdit />
              </PrivateRoute>
            }
          />
          <Route
            path='/master-report/email-template/:templateId/edit'
            element={
              <PrivateRoute>
                <MasterReportEmailTemplateEdit />
              </PrivateRoute>
            }
          />
          <Route
            path='/master-report/email-target-by-asset/add'
            element={
              <PrivateRoute>
                <MasterReportEmailTargetAssetEdit />
              </PrivateRoute>
            }
          />
          <Route
            path='/master-report/email-target-by-asset/:reportEmailTargetAssetId/edit'
            element={
              <PrivateRoute>
                <MasterReportEmailTargetAssetEdit />
              </PrivateRoute>
            }
          />

          {/* Operation Mode Route */}
          <Route
            path='/operation-mode'
            element={
              <PrivateRoute>
                <OperationMode />
              </PrivateRoute>
            }
          />
          <Route
            path='/operation-mode/add'
            element={
              <PrivateRoute>
                <OperationModeCreate />
              </PrivateRoute>
            }
          />
          <Route
            path='/operation-mode/:operationModeId/edit'
            element={
              <PrivateRoute>
                <OperationModeEdit />
              </PrivateRoute>
            }
          />
          {/* Admin Route */}
          <Route
            path='/admin'
            element={
              <PrivateRoute>
                <User userType='admin' />
              </PrivateRoute>
            }
          />
          <Route
            path='/admin/add'
            element={
              <PrivateRoute>
                <UserCreate userType='admin' />
              </PrivateRoute>
            }
          />
          <Route
            path='/admin/:userId/edit'
            element={
              <PrivateRoute permission={['USER.UPDATE']}>
                <UserEdit userType='admin' />
              </PrivateRoute>
            }
          />
          <Route
            path='/admin/:userId'
            element={
              <PrivateRoute>
                <UserDetail userType='admin' />
              </PrivateRoute>
            }
          />
          <Route
            path='/admin/:userId/change-password'
            element={
              <PrivateRoute permission={['USER.UPDATE']}>
                <ChangePasswordUser />
              </PrivateRoute>
            }
          />

          {/* Client Route */}
          <Route
            path='/client'
            element={
              <PrivateRoute>
                <Client />
              </PrivateRoute>
            }
          />

          {/* Role Route */}
          <Route
            path='/role'
            element={
              <PrivateRoute>
                <Role />
              </PrivateRoute>
            }
          />
          <Route
            path='/role/add'
            element={
              <PrivateRoute>
                <RoleEdit />
              </PrivateRoute>
            }
          />
          <Route
            path='/role/:roleId/edit'
            element={
              <PrivateRoute>
                <RoleEdit />
              </PrivateRoute>
            }
          />
          <Route
            path='/role/:roleId'
            element={
              <PrivateRoute>
                <RoleDetail />
              </PrivateRoute>
            }
          />

          <Route
            path='/users/:userId/address/:addressId/edit'
            element={
              <PrivateRoute>
                <UserAddressEdit />
              </PrivateRoute>
            }
          />
          <Route
            path='/users/:userId/address/:addressId'
            element={
              <PrivateRoute>
                <UserAddressDetail />
              </PrivateRoute>
            }
          />
          <Route
            path='/privacy-policy'
            element={
              <PrivateRoute>
                <PrivacyPolicy />
              </PrivateRoute>
            }
          />
          <Route
            path='/terms-and-conditions'
            element={
              <PrivateRoute>
                <TermsAndConditions />
              </PrivateRoute>
            }
          />

          <Route
            path='/logout'
            element={
              <PublicRoute>
                <Logout />
              </PublicRoute>
            }
          />
        </Route>
      </Routes>
    </BrowserRouter>
  );
};
export default RootNavigator;
